import {
  AI_ASSISTANT_ACTIVE_CHAT_SESSION_STORAGE_KEY,
  AI_ASSISTANT_DISPLAYED_LAST_CHOICE_LOCAL_STORAGE_KEY,
  AI_ASSISTANT_DISPLAYED_SESSION_STORAGE_KEY,
} from '@/aiAssistant/aiAssistantStorage.utilities';
import { sqAiAssistantStore } from '@/core/core.stores';
import { logWarn } from '@/utilities/logger';
import type { ExternalAgentBuilderPrompt } from '@/agentBuilder/agentBuilder.types';
import type {
  AIMemory,
  AgentType,
  AskUserAnswer,
  AskUserDraft,
  AskUserQuestion,
  ChatAttachment,
  ChatMessage,
  ExternalAIAssistantPayload,
  ExternalGuidedActionBoardEditPayload,
  ExternalGuidedDrillDimensionPayload,
  ExternalPromptPayload,
  FontSize,
  ReasoningEffort,
  ReasoningStyle,
  SelectedDocument,
} from '@/aiAssistant/aiAssistant.types';

export function setAIAssistantDisplayed(displayed: boolean, ignore = false) {
  if (ignore && typeof window !== 'undefined') {
    try {
      // Don't override a prior default:
      // - per-tab choice in sessionStorage, or
      // - cross-tab "last choice" in localStorage.
      if (
        window.sessionStorage.getItem(AI_ASSISTANT_DISPLAYED_SESSION_STORAGE_KEY) !== null ||
        window.localStorage.getItem(AI_ASSISTANT_DISPLAYED_LAST_CHOICE_LOCAL_STORAGE_KEY) !== null
      ) {
        return;
      }
    } catch (error) {
      logWarn(`Failed to read AI Assistant displayed default state from storage: ${error}`);
    }
  }

  sqAiAssistantStore.actions.setDisplayed(displayed);

  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.setItem(AI_ASSISTANT_DISPLAYED_SESSION_STORAGE_KEY, displayed ? 'true' : 'false');
      window.localStorage.setItem(AI_ASSISTANT_DISPLAYED_LAST_CHOICE_LOCAL_STORAGE_KEY, displayed ? 'true' : 'false');
    } catch (error) {
      logWarn(`Failed to persist AI Assistant displayed state to storage: ${error}`);
    }
  }
}

export function setAIAssistantMinimized(minimized: boolean) {
  sqAiAssistantStore.actions.setMinimized(minimized);
}

export function setAIAssistantMaximized(maximized: boolean) {
  sqAiAssistantStore.actions.setMaximized(maximized);
}

export function setAIAssistantLibraryDisplayed(libraryDisplayed: boolean) {
  sqAiAssistantStore.actions.setLibraryDisplayed(libraryDisplayed);
}

export function setAIAssistantMessages(messages: ChatMessage[]) {
  sqAiAssistantStore.actions.setMessages(messages);
}

export function setSelectedAgent(agent: AgentType) {
  sqAiAssistantStore.actions.setSelectedAgent(agent);
}

export function setAIAssistantFontSize(fontSize: FontSize) {
  sqAiAssistantStore.actions.setFontSize(fontSize);
}

export function setAIAssistantExternalPrompt(payload: ExternalAIAssistantPayload | undefined) {
  sqAiAssistantStore.actions.setExternalPrompt(payload);
  if (payload?.kind === 'guidedActionBoardEdit' || payload?.kind === 'guidedDrillDimension') {
    setSelectedAgent('actions');
  } else if (payload?.agent) {
    setSelectedAgent(payload.agent);
  }
}

function openAIAssistantWithExternalPayload(
  payload: ExternalAIAssistantPayload,
  options?: { wasDisplayed?: boolean; mountDelayMs?: number },
) {
  const wasDisplayed = options?.wasDisplayed ?? false;
  const mountDelayMs = options?.mountDelayMs ?? 0;

  if (!wasDisplayed) {
    setAIAssistantDisplayed(true);
  }

  const shouldDelayPrompt = !wasDisplayed && mountDelayMs > 0;

  if (shouldDelayPrompt) {
    setTimeout(() => setAIAssistantExternalPrompt(payload), mountDelayMs);
    return;
  }

  setAIAssistantExternalPrompt(payload);
}

export function openAIAssistantWithExternalPrompt(
  payload: ExternalPromptPayload,
  options?: { wasDisplayed?: boolean; mountDelayMs?: number },
) {
  openAIAssistantWithExternalPayload(payload, options);
}

export function openAIAssistantWithGuidedActionBoardEdit(
  payload: ExternalGuidedActionBoardEditPayload,
  options?: { wasDisplayed?: boolean; mountDelayMs?: number },
) {
  openAIAssistantWithExternalPayload(payload, options);
}

export function openAIAssistantWithGuidedDrillDimension(
  payload: ExternalGuidedDrillDimensionPayload,
  options?: { wasDisplayed?: boolean; mountDelayMs?: number },
) {
  openAIAssistantWithExternalPayload(payload, options);
}

/**
 * Sets an external agent builder prompt that will open AI Assistant in agent builder mode
 * and automatically submit the prompt to the specified agent builder.
 * @param payload The agent builder and prompt to submit, or undefined to clear
 */
export function setAIAssistantExternalAgentBuilderPrompt(payload: ExternalAgentBuilderPrompt | undefined) {
  sqAiAssistantStore.actions.setExternalAgentBuilderPrompt(payload);
}

export function setAIAssistantReasoningStyle(reasoningStyle: ReasoningStyle) {
  sqAiAssistantStore.actions.setReasoningStyle(reasoningStyle);
}

// Undefined uses the selected agent's default.
export function setAIAssistantReasoningEffort(reasoningEffort: ReasoningEffort | undefined) {
  sqAiAssistantStore.actions.setReasoningEffort(reasoningEffort);
}

export function setAIAssistantGenerateMemory(generateMemory: boolean) {
  sqAiAssistantStore.actions.setGenerateMemory(generateMemory);
}

export function setAIAssistantStartNewChatOnOpen(startNewChatOnOpen: boolean) {
  sqAiAssistantStore.actions.setStartNewChatOnOpen(startNewChatOnOpen);
}

/**
 * Sets the preferred subsection of the AI preferences tab
 * Mainly for the redirecting of memories in the memory ui
 * @param subsection The subsection to set
 */
export function setAIAssistantPreferredSubsection(subsection: 'general' | 'memories' | 'privacy' | null) {
  sqAiAssistantStore.actions.setPreferredSubsection(subsection);
}

export function setAIAssistantSavedMemories(savedMemories: AIMemory[]) {
  sqAiAssistantStore.actions.setSavedMemories(savedMemories);
}

export function addAIAssistantMemory(memory: AIMemory) {
  sqAiAssistantStore.actions.addMemory(memory);
}

export function removeAIAssistantMemory(memoryId: string) {
  sqAiAssistantStore.actions.removeMemory(memoryId);
}

export function updateAIAssistantMemory(memory: AIMemory) {
  sqAiAssistantStore.actions.updateMemory(memory);
}

export function setAiAssistantRefreshHistory(refreshHistory: boolean) {
  sqAiAssistantStore.actions.setRefreshHistory(refreshHistory);
}

export function setAiAssistantActiveChat(chat: { id: string; name: string } | undefined) {
  sqAiAssistantStore.actions.setActiveChat(chat);

  if (typeof window !== 'undefined') {
    try {
      if (chat) {
        window.sessionStorage.setItem(AI_ASSISTANT_ACTIVE_CHAT_SESSION_STORAGE_KEY, JSON.stringify(chat));
      } else {
        window.sessionStorage.removeItem(AI_ASSISTANT_ACTIVE_CHAT_SESSION_STORAGE_KEY);
      }
    } catch (error) {
      logWarn(`Failed to persist AI Assistant active chat to sessionStorage: ${error}`);
    }
  }
}

export function setAiAssistantHistorySearch(searchValue: string) {
  sqAiAssistantStore.actions.setHistorySearchValue(searchValue);
}

/**
 * Sets the selected document to reference in the AI Assistant
 * @param document The document to reference or null to clear
 */
export function setSelectedDocument(document: SelectedDocument | SelectedDocument[] | null) {
  sqAiAssistantStore.actions.setSelectedDocument(document);
}

/**
 * Adds a draft attachment to the AI Assistant
 * @param attachment The attachment to add
 */
export function addDraftAttachment(attachment: ChatAttachment) {
  sqAiAssistantStore.actions.addDraftAttachment(attachment);
}

/**
 * Removes a draft attachment from the AI Assistant
 * @param attachmentId The ID of the attachment to remove
 */
export function removeDraftAttachment(attachmentId: string) {
  sqAiAssistantStore.actions.removeDraftAttachment(attachmentId);
}

/**
 * Clears all draft attachments from the AI Assistant
 */
export function clearDraftAttachments() {
  sqAiAssistantStore.actions.clearDraftAttachments();
}

export function setAIAssistantLibraryEditorState(id: string | null, libraryEditorDisplayed: boolean) {
  sqAiAssistantStore.actions.setLibraryEditorState(id, libraryEditorDisplayed);
}

/**
 * Adds a pending ask_user question to the store.
 */
export function addAskUserQuestion(question: AskUserQuestion) {
  sqAiAssistantStore.actions.addAskUserQuestion(question);
}

/**
 * Updates the draft (selected letter or 'Other' freeform text) for a question that's still open in the panel.
 */
export function updateAskUserDraft(questionId: string, draft: AskUserDraft) {
  sqAiAssistantStore.actions.updateAskUserDraft(questionId, draft);
}

/**
 * Fully replaces the draft for a question (used to deselect a choice).
 * Pass an empty draft `{}` to clear all selections for the question.
 */
export function clearAskUserDraft(questionId: string) {
  sqAiAssistantStore.actions.updateAskUserDraft(questionId, {}, true);
}

/**
 * Marks a question as answered/skipped and replaces its draft with the final answer.
 */
export function resolveAskUserQuestion(questionId: string, answer: AskUserAnswer) {
  sqAiAssistantStore.actions.resolveAskUserQuestion(questionId, answer);
}

/**
 * Removes all pending ask_user questions for the given chat (e.g., on stream abort).
 */
export function cancelAskUserQuestionsForChat(chatId: string) {
  if (!chatId) return;
  sqAiAssistantStore.actions.cancelAskUserQuestionsForChat(chatId);
}

/**
 * Toggles the docked Questions panel between collapsed and expanded.
 */
export function setAskUserPanelCollapsed(collapsed: boolean) {
  sqAiAssistantStore.actions.setAskUserPanelCollapsed(collapsed);
}
